import { useState } from "react";
import { STATUS_META } from "@/lib/orbit";

// Signature ORBIT visualisation: the business at the centre of a 10-domain
// transformation system. Radial, precise, interactive — not a cartoon planet.
export function OrbitMap({ domains = [], businessName = "Your Business", onSelect, selectedKey, size = 460 }) {
  const [hovered, setHovered] = useState(null);
  const cx = size / 2;
  const cy = size / 2;
  const radius = size * 0.36;
  const nodeR = size * 0.052;

  const positioned = domains.map((d, i) => {
    const angle = (i / domains.length) * Math.PI * 2 - Math.PI / 2;
    return { ...d, x: cx + radius * Math.cos(angle), y: cy + radius * Math.sin(angle), angle };
  });

  const active = hovered ? positioned.find((d) => d.key === hovered) : null;

  return (
    <div className="relative w-full" style={{ maxWidth: size }} data-testid="orbit-map">
      <svg viewBox={`0 0 ${size} ${size}`} className="w-full h-auto overflow-visible">
        {[0.5, 0.72, 0.92].map((f, i) => (
          <circle key={f} cx={cx} cy={cy} r={radius * f} fill="none" stroke="hsl(var(--border))" strokeWidth="1" strokeDasharray={i === 2 ? "2 5" : "0"} />
        ))}
        {positioned.map((d) => {
          const meta = STATUS_META[d.status] || STATUS_META.Unknown;
          const isHot = hovered === d.key || selectedKey === d.key;
          return (
            <line
              key={`l-${d.key}`}
              x1={cx}
              y1={cy}
              x2={d.x}
              y2={d.y}
              stroke={isHot ? meta.color : "hsl(var(--border))"}
              strokeWidth={isHot ? 2 : 1}
              strokeOpacity={isHot ? 0.9 : 0.55}
              style={{ transition: "stroke 200ms, stroke-width 200ms" }}
            />
          );
        })}

        {/* Centre business core */}
        <circle cx={cx} cy={cy} r={nodeR * 1.5} fill="hsl(var(--primary))" />
        <text x={cx} y={cy - 3} textAnchor="middle" className="font-num" fontSize={size * 0.03} fontWeight="700" fill="hsl(var(--primary-foreground))">
          BUSINESS
        </text>
        <text x={cx} y={cy + size * 0.032} textAnchor="middle" fontSize={size * 0.02} fill="hsl(var(--primary-foreground))" opacity="0.7">
          Core
        </text>

        {positioned.map((d) => {
          const meta = STATUS_META[d.status] || STATUS_META.Unknown;
          const isHot = hovered === d.key || selectedKey === d.key;
          const labelOnRight = d.x >= cx - 4;
          return (
            <g
              key={d.key}
              style={{ cursor: onSelect ? "pointer" : "default" }}
              onMouseEnter={() => setHovered(d.key)}
              onMouseLeave={() => setHovered(null)}
              onClick={() => onSelect && onSelect(d)}
              data-testid={`orbit-node-${d.key}`}
            >
              <circle cx={d.x} cy={d.y} r={nodeR + (isHot ? 4 : 0)} fill="hsl(var(--card))" stroke={meta.color} strokeWidth={isHot ? 3 : 2} style={{ transition: "r 180ms" }} />
              <circle cx={d.x} cy={d.y} r={nodeR * 0.42} fill={meta.color} />
              <text
                x={labelOnRight ? d.x + nodeR + 6 : d.x - nodeR - 6}
                y={d.y + 4}
                textAnchor={labelOnRight ? "start" : "end"}
                fontSize={size * 0.026}
                fontWeight="600"
                fill="hsl(var(--foreground))"
              >
                {d.label}
              </text>
            </g>
          );
        })}
      </svg>

      {active && (
        <div className="pointer-events-none absolute left-1/2 top-2 -translate-x-1/2 rounded-lg border border-border bg-popover px-3 py-2 text-center shadow-lg" data-testid="orbit-tooltip">
          <div className="flex items-center justify-center gap-2">
            <span className="h-2 w-2 rounded-full" style={{ backgroundColor: (STATUS_META[active.status] || STATUS_META.Unknown).color }} />
            <span className="text-sm font-semibold">{active.label}</span>
            <span className="font-num text-sm text-muted-foreground">· {active.score}</span>
          </div>
          <div className="mt-0.5 text-xs font-medium" style={{ color: (STATUS_META[active.status] || STATUS_META.Unknown).color }}>
            {active.status}
          </div>
        </div>
      )}
    </div>
  );
}
